import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API = process.env.REACT_APP_API_URL || "https://doctor-appointment-1-yf2j.onrender.com";

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h);
  const period = hour >= 12 ? "PM" : "AM";
  return `${hour > 12 ? hour - 12 : hour || 12}:${m} ${period}`;
};

export default function DoctorNotifications({ doctor }) {
  const [upcoming, setUpcoming] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  const fetchNotifications = useCallback(async () => {
    if (!doctor?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/doctor-portal/${doctor.id}/notifications`
      );
      setUpcoming(res.data.upcoming || []);
      setRequests(res.data.requests || []);
    } catch (err) {
      toast.error("Failed to load notifications.");
    } finally {
      setLoading(false);
    }
  }, [doctor?.id]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleResponse = async (appointmentId, action) => {
    setProcessingId(appointmentId);
    try {
      await axios.post(
        `${API}/api/doctor-portal/appointments/${appointmentId}/reassign/confirm`,
        { action, doctorId: doctor.id }
      );
      toast.success(
        action === "accepted"
          ? "✅ Reassignment accepted — waiting for patient confirmation."
          : "❌ Reassignment declined."
      );
      fetchNotifications();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to process request.");
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "16px 16px 80px", fontFamily: "'Outfit', sans-serif" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div style={{ marginBottom: 36 }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, color: "#0c4a6e", margin: "0 0 6px" }}>
          🔔 Notifications
        </h1>
        <p style={{ fontSize: 14, color: "#64748b", margin: 0 }}>
          Stay on top of your schedule and incoming requests.
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8", fontSize: 16, fontWeight: 600 }}>
          Loading notifications...
        </div>
      ) : (
        <>
          {/* ── Reassignment Requests ── */}
          <section style={{ marginBottom: 40 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: "#0c4a6e", margin: 0 }}>
                Reassignment Requests
              </h2>
              {requests.length > 0 && (
                <span style={{
                  background: "#fef3c7", color: "#d97706", fontWeight: 800,
                  fontSize: 12, padding: "3px 10px", borderRadius: 20, border: "1px solid #fde68a"
                }}>
                  {requests.length} Pending
                </span>
              )}
            </div>

            {requests.length === 0 ? (
              <div style={{
                background: "rgba(255,255,255,0.7)", backdropFilter: "blur(12px)",
                borderRadius: 20, padding: "40px 32px", textAlign: "center",
                border: "2px dashed #bae6fd", color: "#94a3b8", fontWeight: 600
              }}>
                🎉 No pending reassignment requests
              </div>
            ) : (
              requests.map((req) => (
                <div key={req.id} style={{
                  background: "rgba(255,255,255,0.88)", backdropFilter: "blur(16px)",
                  borderRadius: 20, padding: "24px 28px", marginBottom: 16,
                  border: "1px solid #fde68a", borderLeft: "5px solid #f59e0b",
                  boxShadow: "0 8px 28px rgba(245,158,11,0.1)",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  flexWrap: "wrap", gap: 16
                }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontWeight: 800, fontSize: 17, color: "#0f172a", margin: "0 0 8px" }}>
                      Dr. {req.requested_by_name} → Transfer Request
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 24px", fontSize: 13, color: "#475569" }}>
                      <span>👤 {req.patient_name}</span>
                      <span>📅 {formatDate(req.date)} at {formatTime(req.time)}</span>
                      <span>⏱ {req.duration} mins</span>
                      <span>📋 {req.appointment_type === "online" ? "Online" : "In-Person"}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 12 }}>
                    <button
                      disabled={processingId === req.id}
                      onClick={() => handleResponse(req.id, "declined")}
                      style={{
                        padding: "10px 20px", borderRadius: 12, border: "none", cursor: "pointer",
                        background: "#fee2e2", color: "#b91c1c", fontWeight: 700, fontSize: 13,
                        transition: "all 0.2s", opacity: processingId === req.id ? 0.6 : 1
                      }}
                    >
                      Decline
                    </button>
                    <button
                      disabled={processingId === req.id}
                      onClick={() => handleResponse(req.id, "accepted")}
                      style={{
                        padding: "10px 20px", borderRadius: 12, border: "none", cursor: "pointer",
                        background: "linear-gradient(135deg, #10b981, #059669)",
                        color: "#fff", fontWeight: 700, fontSize: 13,
                        boxShadow: "0 4px 12px rgba(16,185,129,0.22)",
                        transition: "all 0.2s", opacity: processingId === req.id ? 0.6 : 1
                      }}
                    >
                      {processingId === req.id ? "Processing..." : "Accept"}
                    </button>
                  </div>
                </div>
              ))
            )}
          </section>

          {/* ── Upcoming Appointments ── */}
          <section>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: "#0c4a6e", margin: 0 }}>
                Upcoming in 24 Hours
              </h2>
              {upcoming.length > 0 && (
                <span style={{
                  background: "#dbeafe", color: "#1d4ed8", fontWeight: 800,
                  fontSize: 12, padding: "3px 10px", borderRadius: 20, border: "1px solid #bfdbfe"
                }}>
                  {upcoming.length}
                </span>
              )}
            </div>

            {upcoming.length === 0 ? (
              <div style={{
                background: "rgba(255,255,255,0.7)", backdropFilter: "blur(12px)",
                borderRadius: 20, padding: "40px 32px", textAlign: "center",
                border: "2px dashed #bae6fd", color: "#94a3b8", fontWeight: 600
              }}>
                No appointments coming up in the next 24 hours
              </div>
            ) : (
              upcoming.map((appt) => (
                <div key={appt.id} style={{
                  background: "rgba(255,255,255,0.88)", backdropFilter: "blur(16px)",
                  borderRadius: 20, padding: "24px 28px", marginBottom: 16,
                  border: "1px solid #bae6fd", borderLeft: "5px solid #0ea5e9",
                  boxShadow: "0 8px 28px rgba(14,165,233,0.1)",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  flexWrap: "wrap", gap: 16
                }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontWeight: 800, fontSize: 17, color: "#0f172a", margin: "0 0 8px" }}>
                      Appointment with {appt.patient_name}
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 24px", fontSize: 13, color: "#475569" }}>
                      <span>📅 {formatDate(appt.date)} at {formatTime(appt.time)}</span>
                      <span>⏱ {appt.duration} mins</span>
                      <span>📋 {appt.appointment_type === "online" ? "🌐 Online" : "🏥 In-Person"}</span>
                    </div>
                  </div>
                  {appt.meeting_link && (
                    <a
                      href={appt.meeting_link}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        padding: "10px 20px", borderRadius: 12, textDecoration: "none",
                        background: "#f0f9ff", color: "#0369a1", fontWeight: 700, fontSize: 13,
                        border: "1px solid #bae6fd", transition: "all 0.2s",
                        whiteSpace: "nowrap"
                      }}
                    >
                      🎥 Join Meeting
                    </a>
                  )}
                </div>
              ))
            )}
          </section>
        </>
      )}
    </div>
  );
}
